import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { 
  ArrowLeft, 
  Edit, 
  Download, 
  Eye, 
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  DollarSign,
  FileText,
  User,
  Calendar,
  Package,
  TrendingUp,
  TrendingDown,
  RefreshCw
} from 'lucide-react';
import { getChargeback, getChargebackActivity, reconcileChargeback, exportChargebackReport, downloadChargebackReport } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';

const ChargebackDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const reconcileMutation = useMutation({
    mutationFn: (reconciliationData) => reconcileChargeback(id, reconciliationData),
    onSuccess: () => {
      toast.success('Chargeback reconciled');
      queryClient.invalidateQueries({ queryKey: ['chargeback', id] });
      queryClient.invalidateQueries({ queryKey: ['chargeback-activity', id] });
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to reconcile chargeback')
  });

  const handleReconcile = () => {
    const notes = window.prompt('Reconciliation notes (optional):', '');
    if (notes === null) return; // user cancelled
    reconcileMutation.mutate({ reconciliationNotes: notes });
  };

  const saveBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadReport = async () => {
    try {
      const blob = await exportChargebackReport({ chargebackId: id });
      saveBlob(blob, `chargeback-report-${chargeback?.claimNumber || id}.json`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to download report');
    }
  };

  const handleDownloadCsv = async () => {
    try {
      const blob = await downloadChargebackReport(id);
      saveBlob(new Blob([blob], { type: 'text/csv' }), `chargeback-${chargeback?.claimNumber || id}.csv`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to download');
    }
  };

  const { data: chargeback, isLoading, error } = useQuery({
    queryKey: ['chargeback', id],
    queryFn: () => getChargeback(id),
    enabled: !!id && !!user,
    retry: false
  });

  const { data: activity = [] } = useQuery({
    queryKey: ['chargeback-activity', id],
    queryFn: () => getChargebackActivity(id),
    enabled: !!id && !!user
  });

  if (!user) {
    navigate('/app/login');
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-red-800 mb-2">Error Loading Chargeback</h2>
          <p className="text-red-600 mb-4">{error.message || 'Failed to load chargeback details'}</p>
          <button
            onClick={() => navigate('/app/chargebacks')}
            title="Back to chargebacks"
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
          >
            Back to Chargebacks
          </button>
        </div>
      </div>
    );
  }

  if (!chargeback) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-yellow-400 mb-4" />
          <h2 className="text-xl font-semibold text-yellow-800 mb-2">Chargeback Not Found</h2>
          <p className="text-yellow-600 mb-4">The requested chargeback could not be found.</p>
          <button
            onClick={() => navigate('/app/chargebacks')}
            title="Back to chargebacks"
            className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700"
          >
            Back to Chargebacks
          </button>
        </div>
      </div>
    );
  }

  const getStatusInfo = (status) => {
    const statusConfig = {
      IMPORTED: { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'Imported' },
      VALIDATED: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Validated' },
      IN_ERROR: { color: 'bg-red-100 text-red-800', icon: AlertCircle, label: 'In Error' },
      REJECTED: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Rejected' },
      APPROVED: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Approved' },
      POSTED: { color: 'bg-purple-100 text-purple-800', icon: DollarSign, label: 'Posted' },
      SUPERSEDED: { color: 'bg-purple-100 text-purple-800', icon: RefreshCw, label: 'Superseded' },
      CANCELLED: { color: 'bg-slate-200 text-slate-600', icon: XCircle, label: 'Cancelled' }
    };
    
    return statusConfig[status] || { color: 'bg-gray-100 text-gray-800', icon: Clock, label: status };
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const statusInfo = getStatusInfo(chargeback.status);
  const StatusIcon = statusInfo.icon;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/app/chargebacks')}
            title="Back to chargebacks"
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Chargeback Details</h1>
            <p className="text-gray-600">Claim #{chargeback.claimNumber}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/app/chargebacks/${id}/edit`)}
            title="Edit chargeback"
            className="px-4 py-2 text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 flex items-center gap-2"
          >
            <Edit size={16} />
            Edit
          </button>
          <button
            onClick={handleDownloadCsv}
            title="Download CSV"
            className="px-4 py-2 text-green-600 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 flex items-center gap-2"
          >
            <Download size={16} />
            Download
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* EDI 844 resubmission / cancellation banner */}
          {(chargeback.isResubmission || chargeback.purposeCode === '01' || chargeback.supersedesClaim ||
            chargeback.status === 'SUPERSEDED' || chargeback.status === 'CANCELLED') && (
            <div className={`rounded-lg border p-4 flex items-start gap-3 ${
              chargeback.status === 'SUPERSEDED' || chargeback.status === 'CANCELLED'
                ? 'bg-slate-50 border-slate-200'
                : 'bg-amber-50 border-amber-200'}`}>
              <RefreshCw className="w-5 h-5 mt-0.5 text-amber-600 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-semibold text-gray-900">
                  {chargeback.status === 'SUPERSEDED' ? 'This claim was superseded by a later resubmission'
                    : chargeback.status === 'CANCELLED' ? 'This claim was cancelled'
                    : chargeback.purposeCode === '01' ? 'EDI 844 Cancellation request'
                    : `EDI 844 Resubmission (${chargeback.purposeCode === '04' ? 'Change' : chargeback.purposeCode === '05' ? 'Replace' : chargeback.purposeCode === '6R' ? 'Resubmission' : chargeback.purposeCode})`}
                </p>
                {chargeback.supersedesClaim && (
                  <p className="text-gray-700 mt-0.5">
                    {chargeback.purposeCode === '01' ? 'Cancels' : 'Replaces prior claim'}:{' '}
                    <span className="font-mono">{chargeback.supersedesClaim}</span>
                  </p>
                )}
                {chargeback.resubmissionReason && (
                  <p className="text-gray-700 mt-0.5"><span className="font-medium">Reason:</span> {chargeback.resubmissionReason}</p>
                )}
              </div>
            </div>
          )}

          {/* Why this claim errored */}
          {chargeback.status === 'IN_ERROR' && (chargeback.validationErrors?.length > 0 || chargeback.errorReason) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 no-card-hover">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-semibold text-red-800">Why this claim is in error</h3>
                  {chargeback.validationErrors?.length > 0 ? (
                    <ul className="mt-1 list-disc list-inside text-sm text-red-700 space-y-0.5">
                      {chargeback.validationErrors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  ) : (
                    <p className="mt-1 text-sm text-red-700">{chargeback.errorReason}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Claim Information */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Claim Information</h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Claim Number</label>
                  <p className="text-lg font-semibold text-gray-900">{chargeback.claimNumber}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Status</label>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color}`}>
                    <StatusIcon className="w-4 h-4 mr-2" />
                    {statusInfo.label}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Claim Date</label>
                  <p className="text-gray-900">{formatDate(chargeback.claimDate)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Total Amount</label>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(chargeback.totalAmount)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Batch Number</label>
                  <p className="text-gray-900">{chargeback.batch?.batchNumber || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Line Items</label>
                  <p className="text-gray-900">{chargeback.lines?.length || 0} items</p>
                </div>
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Line Items</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Line</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sales Price</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contract Price</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Variance</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {chargeback.lines?.map((line, index) => {
                    const lineStatusInfo = getStatusInfo(line.status);
                    const LineStatusIcon = lineStatusInfo.icon;
                    const variance = line.variance || 0;
                    const variancePercent = line.contractPrice ? (variance / line.contractPrice) * 100 : 0;
                    
                    return (
                      <tr key={line.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-900">{line.lineNo}</td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {line.product?.name || line.productName || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            NDC: {line.productNdc || line.product?.ndc11 || line.product?.productNdc || 'N/A'}
                          </div>
                          {line.notes && (
                            <div className="mt-1 text-xs text-amber-700 flex items-start gap-1">
                              <RefreshCw className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              <span>{line.notes}</span>
                            </div>
                          )}
                          {line.errorReason && (
                            <div className="mt-1 text-xs text-red-700 flex items-start gap-1">
                              <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              <span>{line.errorReason}</span>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">{line.quantity}</td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {formatCurrency(line.salesPrice)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {line.contractPrice ? formatCurrency(line.contractPrice) : 'N/A'}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium ${
                              variance > 0 ? 'text-red-600' : variance < 0 ? 'text-green-600' : 'text-gray-600'
                            }`}>
                              {formatCurrency(Math.abs(variance))}
                            </span>
                            {line.contractPrice && (
                              <span className={`text-xs ${
                                variance > 0 ? 'text-red-500' : variance < 0 ? 'text-green-500' : 'text-gray-500'
                              }`}>
                                ({variancePercent > 0 ? '+' : ''}{variancePercent.toFixed(1)}%)
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {line.validationStatus && !['PENDING', 'VALIDATED', 'MANUALLY_EDITED'].includes(line.validationStatus) ? (
                            <span title={line.errorReason || ''} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              {line.validationStatus}
                            </span>
                          ) : (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${lineStatusInfo.color}`}>
                              <LineStatusIcon className="w-3 h-3 mr-1" />
                              {lineStatusInfo.label}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Customer Information */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Customer Information</h3>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <User className="h-8 w-8 text-primary-600" />
                <div>
                  <h4 className="font-medium text-gray-900">{chargeback.customer?.name || 'N/A'}</h4>
                  <p className="text-sm text-gray-500">{chargeback.customer?.type || 'N/A'}</p>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">EDI Entity ID</label>
                  <p className="text-sm text-gray-900">{chargeback.customer?.ediEntityId || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">NPI Number</label>
                  <p className="text-sm text-gray-900">{chargeback.customer?.npiNumber || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">State License</label>
                  <p className="text-sm text-gray-900">{chargeback.customer?.stateLicense || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contract Information */}
          {chargeback.contract && (
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Contract Information</h3>
              </div>
              <div className="p-6">
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Contract Number</label>
                    <p className="text-sm font-medium text-gray-900">{chargeback.contract.number}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Type</label>
                    <p className="text-sm text-gray-900">{chargeback.contract.type}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Status</label>
                    <p className="text-sm text-gray-900">{chargeback.contract.status}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Effective Dates</label>
                    <p className="text-sm text-gray-900">
                      {formatDate(chargeback.contract.effectiveFrom)} - {formatDate(chargeback.contract.effectiveTo)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Batch Information */}
          {chargeback.batch && (
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Batch Information</h3>
              </div>
              <div className="p-6">
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Batch Number</label>
                    <p className="text-sm font-medium text-gray-900">{chargeback.batch.batchNumber}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Status</label>
                    <p className="text-sm text-gray-900">{chargeback.batch.status}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Record Count</label>
                    <p className="text-sm text-gray-900">{chargeback.batch.recordCount}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Error Count</label>
                    <p className="text-sm text-gray-900">{chargeback.batch.errorCount}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Processed At</label>
                    <p className="text-sm text-gray-900">
                      {chargeback.batch.processedAt ? formatDate(chargeback.batch.processedAt) : 'Not processed'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
            </div>
            <div className="p-6 space-y-3">
              <button
                onClick={() => navigate(`/app/chargebacks/${id}/edit`)}
                title="Edit chargeback"
                className="w-full px-4 py-2 text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 flex items-center justify-center gap-2"
              >
                <Edit size={16} />
                Edit Chargeback
              </button>
              <button
                onClick={handleDownloadReport}
                title="Download report"
                className="w-full px-4 py-2 text-green-600 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 flex items-center justify-center gap-2"
              >
                <Download size={16} />
                Download Report
              </button>
              <button
                onClick={handleReconcile}
                title="Reconcile chargeback"
                disabled={reconcileMutation.isPending || chargeback.status === 'POSTED' || chargeback.status === 'CANCELLED' || chargeback.status === 'SUPERSEDED'}
                className="w-full px-4 py-2 text-purple-600 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FileText size={16} />
                {reconcileMutation.isPending ? 'Reconciling…' : chargeback.status === 'POSTED' ? 'Reconciled' : 'Reconcile'}
              </button>
            </div>
          </div>

          {/* Activity Log — who did what, when */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200 flex items-center gap-2">
              <Clock size={18} className="text-gray-500" />
              <h3 className="text-lg font-semibold text-gray-900">Activity Log</h3>
            </div>
            <div className="p-6">
              {activity.length === 0 ? (
                <p className="text-sm text-gray-500">No recorded activity yet. Actions taken on this claim (validate, approve, reject, edit) will appear here with the user and timestamp.</p>
              ) : (
                <ol className="relative border-l border-gray-200 ml-2 space-y-5">
                  {activity.map((a, i) => (
                    <li key={i} className="ml-4">
                      <div className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full bg-primary-500 border border-white" />
                      <div className="text-sm font-medium text-gray-900">{(a.action || '').replace(/_/g, ' ')}</div>
                      <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                        <User size={12} />
                        <span>{a.by || 'system'}</span>
                        <span>·</span>
                        <span>{a.at ? new Date(a.at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}</span>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChargebackDetail;
